/**
 * ClusterOpportunityBanner — Driver Activation UI (Iter 97 Sprint 1)
 * =====================================================================
 * Shows the driver pending cluster opportunities (e.g. "5 Pendler suchen
 * dich auf Hamburg → Berlin Di 08:00") with 1-click "Fahrt anbieten" action.
 *
 * Polls /api/cluster/opportunities every 60s + on focus.
 * On accept → POST /api/cluster/<id>/accept then navigate to a prefilled
 * /rides/new with the cluster's route + date + suggested seats.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Users, Clock, MapPin, X, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ClusterOpportunityBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [busy, setBusy] = useState({});

  const fetchAll = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await axios.get(`${API}/cluster/opportunities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpportunities(r.data?.opportunities || []);
    } catch (_) { /* silent — best-effort */ }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 60000);
    const onFocus = () => fetchAll();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [fetchAll]);

  const offer = async (opp) => {
    setBusy(b => ({ ...b, [opp.id]: 'accepting' }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cluster/${opp.id}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Navigate to ride-creation with prefilled data
      const params = new URLSearchParams({
        from: opp.from_city, to: opp.to_city,
        date: opp.travel_date,
        time: `${String(opp.travel_hour).padStart(2, '0')}:00`,
        seats: String(opp.pendler_count),
        cluster_id: opp.id,
      });
      navigate(`/rides/new?${params.toString()}`);
    } catch (_) {
      setBusy(b => ({ ...b, [opp.id]: null }));
    }
  };

  const dismiss = async (opp) => {
    setBusy(b => ({ ...b, [opp.id]: 'dismissing' }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cluster/${opp.id}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpportunities(prev => prev.filter(x => x.id !== opp.id));
    } catch (_) {
      setBusy(b => ({ ...b, [opp.id]: null }));
    }
  };

  if (opportunities.length === 0) return null;

  return (
    <div data-testid="cluster-opportunity-banner" style={styles.wrap}>
      <div style={styles.header}>
        <TrendingUp size={18} style={{ color: '#f59e0b' }} />
        <span style={styles.headerText}>
          {t('cluster.headerCount', { count: opportunities.length, defaultValue: '{{count}} aktive Mitfahr-Anfragen' })}
        </span>
      </div>
      <div style={styles.list}>
        {opportunities.slice(0, 3).map(opp => (
          <div key={opp.id} data-testid={`cluster-opp-${opp.id}`} style={styles.card}>
            <div style={styles.cardLeft}>
              <div style={styles.route}>
                <MapPin size={14} style={{ color: '#0ea5e9' }} />
                <strong>{opp.from_city}</strong>
                <ArrowRight size={12} style={{ color: '#94a3b8' }} />
                <strong>{opp.to_city}</strong>
              </div>
              <div style={styles.meta}>
                <span style={styles.metaPill}>
                  <Clock size={11} /> {opp.travel_date} • {String(opp.travel_hour).padStart(2, '0')}:00
                </span>
                <span style={{ ...styles.metaPill, background: '#fef3c7', color: '#92400e' }}>
                  <Users size={11} /> {opp.pendler_count} {t('cluster.pendlersWaiting', 'Pendler suchen dich')}
                </span>
              </div>
            </div>
            <div style={styles.cardActions}>
              <button
                data-testid={`cluster-offer-${opp.id}`}
                onClick={() => offer(opp)}
                disabled={busy[opp.id]}
                style={styles.offerBtn}
              >
                {busy[opp.id] === 'accepting'
                  ? t('cluster.loading', '...')
                  : t('cluster.offerRide', 'Fahrt anbieten')}
              </button>
              <button
                data-testid={`cluster-dismiss-${opp.id}`}
                onClick={() => dismiss(opp)}
                disabled={busy[opp.id]}
                style={styles.dismissBtn}
                title={t('cluster.dismiss', 'Ignorieren')}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    border: '1px solid #fcd34d',
    borderRadius: 14,
    padding: '14px 16px',
    marginBottom: 18,
    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.08)',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
  },
  headerText: {
    fontWeight: 700, fontSize: 14, color: '#78350f',
  },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    background: '#fff',
    border: '1px solid rgba(252, 211, 77, 0.4)',
    borderRadius: 10,
    padding: '12px 14px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 12,
  },
  cardLeft: { flex: 1 },
  route: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 14, color: '#0f172a', marginBottom: 6,
  },
  meta: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  metaPill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 6,
    background: '#f1f5f9', color: '#475569',
    fontSize: 11, fontWeight: 500,
  },
  cardActions: { display: 'flex', alignItems: 'center', gap: 6 },
  offerBtn: {
    padding: '8px 14px', borderRadius: 8,
    background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    border: 'none', color: '#fff',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dismissBtn: {
    width: 30, height: 30, borderRadius: 8,
    background: 'transparent',
    border: '1px solid #cbd5e1', color: '#64748b',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
